import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { NotificationAction, NotificationReducer } from 'types/notification'
import NotificationActions from 'actions/Notification'
import NotificationService from 'services/Notification'
import { Dispatch } from 'redux'
import { Beforeunload } from 'react-beforeunload'
import { Confirm } from 'components/Modals/Confirm'
import { SubTitleText, TitleText } from 'pages/NotifyEvent/styles'
import { IconWarning } from 'components/NavBar/svg'
import GroupDetails from '../GroupActionPlan/Children/GroupDetails'
import { GroupData } from '../GroupActionPlan/helpers'
import { Breadcrumbs, SecondTextBreadcrumbs } from '../../NotificationsTreatmentsSteps/Header/styles'
import { AccountabilityMatrixQuestionFlow } from '../../NotificationsTreatmentsSteps/AccountabilityMatrix/AccountabilityMatrixQuestionFlow'
import {
    BackButton,
    ContainerForm,
    MainForm,
    SaveButton,
    Top,
    TopInputs,
    TitleForm,
    SubtitleForm
} from '../GroupActionPlan/styles'
import { useCallbackPrompt } from 'hooks/useCallbackPrompt'
import { useForm } from 'react-hook-form'
import { WarningModal } from 'components/Modals/WarningModal'
import { AiOutlineExclamationCircle } from 'react-icons/ai'

const GroupAccountabilityMatrixForm: React.FC = () => {
    const [question01, setQuestion01] = useState('')
    const [question02, setQuestion02] = useState('')
    const [question03, setQuestion03] = useState('')
    const [question04, setQuestion04] = useState('')
    const [question05, setQuestion05] = useState('')
    const [question06, setQuestion06] = useState('')
    const [question07, setQuestion07] = useState('')
    const [question08, setQuestion08] = useState('')
    const [question09, setQuestion09] = useState('')
    const [question10, setQuestion10] = useState('')
    const [result, setResult] = useState('')

    const [changeQuestions, setChangeQuestions] = useState<boolean>(false)
    const [warningModal, setWarningModal] = useState<boolean>(false)

    const [isDirty, setIsDirty] = useState(false)

    const { handleSubmit } = useForm();

    const [isSaving, setIsSaving] = useState(false)
    const [groupData, setGroupData] = useState<GroupData | null>(null)

    const { id } = useParams()
    const navigate = useNavigate()
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()
    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )

    const accountabilityMatrixMemo = useMemo(() => {
        if (notificationSelector?.accountabilityMatrix) {
            return notificationSelector.accountabilityMatrix[0]
        }
    }, [notificationSelector?.accountabilityMatrix])

    useEffect(() => {
        const fetchGroupData = async () => {
            if (!id) return
            try {
                const groupResponse = await NotificationService.getGroupDataById(id)
                setGroupData(groupResponse)
            } catch (error) {
                console.error('Erro ao buscar dados do grupo:', error)
            }
        }
        fetchGroupData()
    }, [id])

    useEffect(() => {
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, firstNotificationId)
            }
        }
    }, [groupData, notificationDispatch])

    useEffect(() => {
        if (!accountabilityMatrixMemo) return
        setQuestion01(accountabilityMatrixMemo.u_question_01 || '')
        setQuestion02(accountabilityMatrixMemo.u_question_02 || '')
        setQuestion03(accountabilityMatrixMemo.u_question_03 || '')
        setQuestion04(accountabilityMatrixMemo.u_question_04 || '')
        setQuestion05(accountabilityMatrixMemo.u_question_05 || '')
        setQuestion06(accountabilityMatrixMemo.u_question_06 || '')
        setQuestion07(accountabilityMatrixMemo.u_question_07 || '')
        setQuestion08(accountabilityMatrixMemo.u_question_08 || '')
        setQuestion09(accountabilityMatrixMemo.u_question_09 || '')
        setQuestion10(accountabilityMatrixMemo.u_question_10 || '')
        setResult(accountabilityMatrixMemo.u_matrix_result || '')
    }, [accountabilityMatrixMemo])

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isDirty && !isSaving)

    const backToGroupTreatment = useCallback(() => {
        navigate(`/notificacoes/group-treatment/${id}`, { state: { currentStep: 4 } })
    }, [id, navigate])

    const handleViewNotification = useCallback(
        (notificationId: string) => {
            if (!notificationId || !groupData) return
            const notificationIds =
                groupData.notifications?.map((notification) => notification.sys_id?.value).filter((value) => value) || []
            navigate(`/notificacoes/view-group`, {
                state: {
                    fromGroup: true,
                    groupId: id,
                    notificationIds,
                    initialNotificationId: notificationId,
                    fromAccountabilityEdit: true
                }
            })
        },
        [groupData, id, navigate]
    )

    const handleChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;
        setIsDirty(false)
        setResult('')

        if (name === "u_question_01") {
            setIsDirty(false)
            setQuestion01(value)
            setQuestion02("")
            setQuestion03("")
            setQuestion04("")
            setQuestion05("")
            setQuestion06("")
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
        } else if (name === "u_question_02") {
            setQuestion02(value)
            setQuestion03("")
            setQuestion04("")
            setQuestion05("")
            setQuestion06("")
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
            setIsDirty(value === "yes")
        } else if (name === "u_question_03") {
            setQuestion03(value)
            setQuestion04("")
            setQuestion05("")
            setQuestion06("")
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
            setIsDirty(false)
        } else if (name === "u_question_04") {
            setQuestion04(value)
            setIsDirty(value === "no")
            setQuestion05("")
            setQuestion06("")
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
        } else if (name === "u_question_05") {
            setQuestion05(value)
            setQuestion06("")
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
            setIsDirty(false)
        } else if (name === "u_question_06") {
            setQuestion06(value)
            setQuestion07("")
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
            setIsDirty(false)
        } else if (name === "u_question_07") {
            setQuestion07(value)
            setIsDirty(true)
            setQuestion08("")
            setQuestion09("")
            setQuestion10("")
        } else if (name === "u_question_08") {
            setQuestion08(value)
            setQuestion09("")
            setQuestion10("")
            setIsDirty(false)
        } else if (name === "u_question_09") {
            setQuestion09(value)
            setIsDirty(true)
            if (question08 === "no") {
                setQuestion10("")
            }
        } else if (name === "u_question_10") {
            if (value === "yes") {
                setQuestion09("")
            } else {
                setIsDirty(true)
            }
            setQuestion10(value)
        }
    }

    const handleSubmitForm = async () => {
        if (!groupData?.notifications?.length) return

        setIsDirty(false)
        setIsSaving(true)

        const hasExisting = Boolean(accountabilityMatrixMemo?.sys_id)
        const notificationGroup = groupData.notifications

        const questions = {
            u_question_01: question01,
            u_question_02: question02,
            u_question_03: question03,
            u_question_04: question04,
            u_question_05: question05,
            u_question_06: question06,
            u_question_07: question07,
            u_question_08: question08,
            u_question_09: question09,
            u_question_10: question10
        }

        try {
            const records = await Promise.all(
                notificationGroup.map(async (notificacao) => {

                    const matrix = hasExisting
                        ? await NotificationActions.getAccountabilityMatrixByNotificationId(
                            notificationDispatch,
                            notificacao.sys_id.value
                        )
                        : undefined

                    return {
                        ...(matrix?.sys_id && { sys_id: matrix.sys_id }),
                        u_hospital_incident: notificacao.sys_id,
                        u_do_not_perform_optional: 'false',
                        ...questions,
                        u_matrix_result: result || ''
                    }
                })
            )

            const payload = { records, options: { stopOnError: false } }

            const action = hasExisting
                ? NotificationActions.updateAccountabilityMatrixBatch
                : NotificationActions.createAccountabilityMatrixBatch

            await action(notificationDispatch, payload)

            navigate(`/notificacoes/group-treatment/${id}`, {state: {currentStep: 4}})
        } catch (error) {
            console.error('Erro ao salvar matriz de responsabilização', error)
        } 
    }

    useEffect(() => {
        if (accountabilityMatrixMemo && accountabilityMatrixMemo?.u_matrix_result !== "") {
            let changeQuestions = accountabilityMatrixMemo?.u_question_02 !== question02 ||
                accountabilityMatrixMemo?.u_question_04 !== question04 ||
                accountabilityMatrixMemo?.u_question_07 !== question07 ||
                accountabilityMatrixMemo?.u_question_09 !== question09 ||
                accountabilityMatrixMemo?.u_question_10 !== question10;
            setChangeQuestions(changeQuestions)
        }
    }, [question02, question04, question07, question09, question10])

    useEffect(() => {
        let AccountabilityMatrixSN: any = {
            u_question_01: question01,
            u_question_02: question02,
            u_question_03: question03,
            u_question_04: question04,
            u_question_05: question05,
            u_question_06: question06,
            u_question_07: question07,
            u_question_08: question08,
            u_question_09: question09,
            u_question_10: question10,
        }
        if (isDirty) {
            NotificationActions.getResultAccountabilityMatrix(notificationDispatch, AccountabilityMatrixSN)
                .then((res) => {
                    setResult(res.data.result)
                }).catch((e: any) => {
                    console.log("Ocorreu um erro ao calcular a preview do resultado da matrix", e)
                }).finally(() => {
                    setIsSaving(false)
                })

        }

    }, [question02, question04, question07, question09, question10])

    return (
        <>
            {isDirty && (
                <Beforeunload onBeforeunload={(event) => 'Você fez edições que não forma salvas.'} />
            )}
            <Confirm
                isOpen={showPrompt as boolean}
                handleConfirmModal={confirmNavigation as any}
                handleCloseModal={cancelNavigation as any}
                labelButtomClose={`Sair`}
                labelButtomConfirm={`Continuar edição`}
            >
                <div className='justify-center flex p-1'>
                    <IconWarning></IconWarning>
                </div>
                <TitleText className={'text-yellow text-xl font-semibold justify-center flex'}>
                    Edição não salva
                </TitleText>
                <div className='justify-center flex'>
                    <SubTitleText >
                        <p>Você fez edições que não foram salvas.</p>
                        <p>Se sair, você perderá as edições.</p>
                    </SubTitleText>
                </div>
            </Confirm>

            <WarningModal
                icon={<AiOutlineExclamationCircle size={50} className="text-yellow" />}
                customStyle={{ titleStyle: 'text-yellow' }}
                title="Alterar resposta"
                isOpen={warningModal as boolean}
                subTitle="O fluxo será atualizado e todas as respostas anteriormente salvas serão perdidas."
                leftButtonText="Cancelar"
                leftButtonAction={() => setWarningModal(false)}
                handleCloseModal={() => setWarningModal(false)}
                rightButtonText="Alterar resposta"
                rightButtonAction={() => handleSubmitForm()}
            />
            <ContainerForm data-testid="test-group-accountability-form">
                <MainForm className="min-w-0 w-full max-w-full" onSubmit={handleSubmit(handleSubmitForm)}>
                    <div className="flex items-baseline mt-8 justify-between">
                        <Breadcrumbs>
                            <Link className="mr-1 cursor-pointer" to="/notificacoes">
                                Gestão das notificações
                            </Link>
                            {' > '}
                            <span className="mr-1 cursor-pointer" onClick={backToGroupTreatment}>
                                Tratamento em Grupo
                            </span>
                            {' > '}
                            <SecondTextBreadcrumbs>Matriz de responsabilização</SecondTextBreadcrumbs>
                        </Breadcrumbs>

                        <TopInputs>
                            <BackButton type="button" onClick={backToGroupTreatment}>Voltar</BackButton>
                            {changeQuestions ?
                                (<SaveButton type='button' onClick={() => setWarningModal(true)} disabled={!isDirty} className={!isDirty ? 'bg-disabled' : ''}>
                                    Salvar
                                </SaveButton>) :
                                (<SaveButton type='submit' disabled={!isDirty} className={!isDirty ? 'bg-disabled' : ''}>
                                    Salvar
                                </SaveButton>)}
                        </TopInputs>
                    </div>

                    <div className="overflow-y-auto min-h-0 w-full min-w-0">
                        {/* <Top>
                            <div>
                                <TitleForm>Matriz de responsabilização em massa</TitleForm>
                                <SubtitleForm>
                                    Defina a análise da matriz para replicar o mesmo resultado para as notificações selecionadas do grupo.
                                </SubtitleForm>
                            </div>
                        </Top> */}

                        <div className="mt-2 flex w-full min-w-0 max-w-full flex-col gap-6 px-8 md:flex-row md:items-start md:gap-8">
                            <div className="order-1 min-w-0 flex-1 basis-0 overflow-x-hidden md:min-h-0">
                                <AccountabilityMatrixQuestionFlow
                                    variant="group"
                                    question01={question01}
                                    question02={question02}
                                    question03={question03}
                                    question04={question04}
                                    question05={question05}
                                    question06={question06}
                                    question07={question07}
                                    question08={question08}
                                    question09={question09}
                                    question10={question10}
                                    result={result}
                                    handleChange={handleChange}
                                />
                            </div>

                            <div className="order-2 w-full shrink-0 md:w-[min(380px,calc(100%-1rem))] md:max-w-[380px] md:sticky md:top-4 md:self-start">
                                <GroupDetails groupData={groupData} onViewNotification={handleViewNotification} />
                            </div>
                        </div>
                    </div>
                </MainForm>
            </ContainerForm>
        </>
    )
}

export { GroupAccountabilityMatrixForm }
